import * as React from 'react';

import {CardContentWithHeader} from 'shells/card/card-content-with-header';
import {Checkbox} from 'shells/checkbox/checkbox';
import {FormField} from 'shells/form/form-field';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SettingsHeader} from 'shells/settings/settings-header';
import {TextField} from 'shells/textfield/textfield';

import {useGetGtmId} from './hooks/use-get-gtm-id';
import {useGetIsAnalyticsDisabled} from './hooks/use-get-is-analytics-disabled';

export function LandingPagesSettings() {
    const {gtmId: gtmIdSettingValue, isLoading: gtmLoading, onUpdateGtmId} = useGetGtmId();
    const {
        isAnalyticsDisabled: isAnalyticsDisabledSettingValue,
        isLoading: analyticsLoading,
        onUpdateIsAnalyticsDisabled,
    } = useGetIsAnalyticsDisabled();
    const [errorMessage, setErrorMessage] = React.useState('');
    const [isSaving, setIsSaving] = React.useState(false);
    const [gtmId, setGtmId] = React.useState<string | null | undefined>(gtmIdSettingValue);
    const [isAnalyticsDisabled, setAnalyticsDisabled] = React.useState<boolean>(
        isAnalyticsDisabledSettingValue
    );

    React.useEffect(() => {
        setGtmId(gtmIdSettingValue);
    }, [gtmIdSettingValue]);

    React.useEffect(() => {
        setAnalyticsDisabled(isAnalyticsDisabledSettingValue);
    }, [isAnalyticsDisabledSettingValue]);

    return (
        <div>
            <SettingsHeader
                title='Settings'
                shouldHideHeader={true}
                onSave={() => {
                    setIsSaving(true);
                    Promise.all([
                        gtmId != null && onUpdateGtmId(gtmId),
                        onUpdateIsAnalyticsDisabled(isAnalyticsDisabled),
                    ])
                        .then(() => {
                            setIsSaving(false);
                        })
                        .catch((error) => {
                            setIsSaving(false);
                            setErrorMessage(error.message);
                        });
                }}
                isSaving={isSaving}
                error={errorMessage}
                disabled={false}
            />
            {gtmLoading || analyticsLoading ? (
                <LoadingPage padding={0} />
            ) : (
                <StackLegacy spacing={16}>
                    <CardContentWithHeader
                        fitContent={false}
                        title='Google Tag Manager'
                        subtitle={
                            <div>
                                Do you use{' '}
                                <Link
                                    href='https://tagmanager.google.com/'
                                    newTab={true}
                                    variant='primary'
                                    size='small'
                                >
                                    Google Tag Manager
                                </Link>
                                ? Add your ID below to have it load on every landing page.
                            </div>
                        }
                        content={
                            <FormField innerId='gtmId' label='GTM ID' errorMessage={errorMessage}>
                                <TextField
                                    placeholder='GTM-123ABC'
                                    value={gtmId || ''}
                                    onChange={setGtmId}
                                    // @ts-expect-error
                                    name='gtmId'
                                />
                            </FormField>
                        }
                    />
                    {NutClientConfig.hasLandingPagesAnalyticsToggle ? (
                        <CardContentWithHeader
                            fitContent={false}
                            title='Lead attribution via Nutshell Analytics'
                            subtitle='Nutshell Analytics is a powerful tool that helps you track leads from your landing pages.'
                            content={
                                <FormField innerId='disableAnalytics' errorMessage={errorMessage}>
                                    <Checkbox
                                        label='Track page views via Nutshell Analytics'
                                        checked={!isAnalyticsDisabled}
                                        onChange={(checked) => setAnalyticsDisabled(!checked)}
                                        // @ts-expect-error
                                        name='disableAnalytics'
                                    />
                                </FormField>
                            }
                        />
                    ) : undefined}
                </StackLegacy>
            )}
        </div>
    );
}
